import { FileStack } from 'lucide-react';
import { BookCover } from './BookCover';
import { LikeButton } from './LikeButton';
import { categoryStyle, initialOf } from '../../lib/summaries';

export const CategoryChip = ({ name, className = '' }) => {
  if (!name) return null;
  const { icon: Icon, chip } = categoryStyle(name);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold w-fit ${chip} ${className}`}>
      <Icon className="w-3 h-3" /> {name}
    </span>
  );
};

export const ReaderAvatar = ({ item, size = 'w-6 h-6 text-[10px]' }) => (
  <span className={`${size} rounded-full bg-[#C19A6B]/20 text-[#5D4037] font-bold flex items-center justify-center border border-[#C19A6B]/30 shrink-0`} aria-hidden="true">
    {item.anonymous ? '•' : initialOf(item.reader_display)}
  </span>
);

export const SummaryCard = ({ item, onOpen, onLike, onShowLikers }) => (
  <article
    role="button"
    tabIndex={0}
    onClick={() => onOpen(item)}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(item); } }}
    className="group break-inside-avoid mb-4 md:mb-6 bg-card rounded-2xl border border-[#C19A6B]/15 p-3 shadow-[0_2px_10px_-6px_rgba(93,64,55,0.25)] hover:-translate-y-1 hover:shadow-[0_14px_28px_-14px_rgba(93,64,55,0.45)] transition-[transform,box-shadow] duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C19A6B]"
    data-testid={`summary-card-${item.id}`}
  >
    <BookCover item={item} />
    <div className="pt-3 px-1 space-y-2 text-right">
      <h3 className="font-bold text-sm md:text-[15px] text-accent leading-snug line-clamp-2 break-words group-hover:text-[#A07A4E] transition-colors duration-150">{item.title || 'بلا عنوان'}</h3>
      {item.author && <p className="text-xs text-muted-foreground line-clamp-1">{item.author}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        <CategoryChip name={item.category} />
        {item.total_pages > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><FileStack className="w-3 h-3" />{item.total_pages} ص</span>
        )}
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-[#C19A6B]/10">
        <div className="flex items-center gap-1.5 min-w-0">
          <ReaderAvatar item={item} />
          <span className="text-[11px] text-muted-foreground truncate">{item.reader_display}</span>
        </div>
        <LikeButton liked={item.liked_by_me} count={item.likes_count} onToggle={() => onLike(item)} onShowLikers={() => onShowLikers(item)} testId={`summary-like-${item.id}`} />
      </div>
    </div>
  </article>
);

export const CoverSkeleton = () => (
  <div className="break-inside-avoid mb-4 md:mb-6 bg-card rounded-2xl border border-[#C19A6B]/10 p-3" data-testid="summary-skeleton">
    <div className="aspect-[2/3] rounded-md bg-gradient-to-br from-[#E8E0D5] to-[#ddd0bf] animate-pulse" />
    <div className="mt-3 h-3 w-4/5 rounded bg-[#E8E0D5] animate-pulse" />
    <div className="mt-2 h-3 w-1/2 rounded bg-[#E8E0D5] animate-pulse" />
  </div>
);

export const EmptyShelf = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center gap-4" data-testid="summaries-empty">
    <svg viewBox="0 0 240 120" className="w-56 text-[#C19A6B]" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="20" y="88" width="200" height="8" rx="2" fill="currentColor" fillOpacity="0.15" />
      <path d="M40 96v14M200 96v14" strokeLinecap="round" />
      <rect x="150" y="48" width="14" height="40" rx="2" strokeOpacity="0.5" />
      <rect x="166" y="56" width="12" height="32" rx="2" strokeOpacity="0.35" transform="rotate(12 172 72)" />
    </svg>
    <p className="text-lg font-bold text-accent">الرف فارغ هنا</p>
    <p className="text-sm text-muted-foreground max-w-xs">لم نجد ملخصات تطابق بحثك. جرّب كلمات أخرى أو امسح بعض الفلاتر.</p>
  </div>
);
